import React, { useState } from 'react';
import {
    Button,
    Typography,
    Box,
    Menu,
    MenuItem,
    IconButton,
    Tooltip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDrag } from 'react-dnd';

const STATUS_COLORS = {
    "No Good": "#D83B50",     // Red
    "Pretty Mid": "#00A9CC",  // Blue
    "Final Draft": "#3EA247", // Green
    "Initial": "#F1C400"      // Yellow (uncategorized)
};

// FIG �Ice Green� gradient (light, readable with dark text)
const FIG_ICE_GRADIENT = 'linear-gradient(135deg, #EBFFF8 0%, #CFF7EA 45%, #A9F0D1 100%)';
const FIG_ICE_GRADIENT_HOVER = 'linear-gradient(135deg, #E2FFF5 0%, #C4F2E4 45%, #9BEAC9 100%)';

/**
 * Props:
 * - id?: string
 * - content: string
 * - initialStatus?: "Initial" | "No Good" | "Pretty Mid" | "Final Draft"
 * - variant?: "figIce" | "status"
 *    -> "figIce": use Ice Green gradient when status === "Initial"; otherwise status color
 *    -> "status": always use STATUS_COLORS[status]
 */
const TaskButton = ({
    id,
    content,
    initialStatus = "Initial",
    variant = "status",
}) => {
    const [status, setStatus] = useState(initialStatus);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isClosed, setIsClosed] = useState(false);
    const [copyTooltip, setCopyTooltip] = useState("Copy");

    // react-dnd
    const [{ isDragging }, dragRef] = useDrag({
        type: 'task',
        item: { id, content, status, variant },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
        end: (_, monitor) => {
            if (monitor.didDrop()) setIsClosed(true);
        }
    });

    // actions
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(content);
            setCopyTooltip("Copied!");
            setTimeout(() => setCopyTooltip("Copy"), 900);
        } catch (err) {
            console.error("Copy failed", err);
        }
    };
    const handleMenuOpen = e => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleStatusChange = s => { setStatus(s); handleMenuClose(); };
    const handleToggleClose = () => setIsClosed(c => !c);

    // render decisions
    const isUncategorized = status === "Initial";
    const useIce = variant === "figIce" && isUncategorized;

    const tileBg = useIce ? FIG_ICE_GRADIENT : STATUS_COLORS[status];
    const tileBgHover = useIce ? FIG_ICE_GRADIENT_HOVER : STATUS_COLORS[status];
    const textColor = useIce ? '#083B2C' : '#fff'; // dark green on light gradient; white on solid

    const truncated = content.length > 160 ? content.slice(0, 160) + '�' : content;
    const displayText = isClosed ? truncated : content;

    return (
        <Box
            ref={dragRef}
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                mb: 1.25,
                opacity: isDragging ? 0.5 : 1,
                cursor: 'grab',
            }}
        >
            {/* Close/Reopen */}
            <Tooltip title={isClosed ? "Reopen text" : "Close text"} arrow>
                <IconButton
                    onClick={handleToggleClose}
                    sx={{
                        color: isClosed ? STATUS_COLORS["Final Draft"] : STATUS_COLORS["No Good"],
                        mr: 1,
                        alignSelf: 'center'
                    }}
                >
                    {isClosed ? <RefreshIcon fontSize="small" /> : <CloseFullscreenIcon fontSize="small" />}
                </IconButton>
            </Tooltip>

            {/* Copy */}
            <Tooltip title={copyTooltip} arrow>
                <IconButton
                    onClick={handleCopy}
                    sx={{ color: STATUS_COLORS["Pretty Mid"], mr: 1, alignSelf: 'center' }}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Status menu */}
            <Tooltip title="Change status" arrow>
                <IconButton
                    onClick={handleMenuOpen}
                    sx={{ color: STATUS_COLORS["Initial"], mr: 1, alignSelf: 'center' }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Main tile */}
            <Button
                variant="contained"
                fullWidth
                sx={{
                    backgroundImage: tileBg,
                    backgroundColor: tileBg,
                    color: textColor,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    p: 1.25,
                    borderRadius: 1.25,
                    border: useIce ? '1px solid #DDF7EF' : '1px solid rgba(0,0,0,0.06)',
                    boxShadow: useIce ? '0 2px 6px rgba(0,0,0,0.06)' : '0 2px 6px rgba(0,0,0,0.12)',
                    '&:hover': {
                        backgroundImage: tileBgHover,
                        backgroundColor: tileBgHover,
                        boxShadow: useIce ? '0 4px 12px rgba(0,0,0,0.08)' : '0 4px 12px rgba(0,0,0,0.18)'
                    }
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '14px',
                        color: textColor,
                        textAlign: 'left',
                        flexGrow: 1,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.35
                    }}
                >
                    {displayText}
                </Typography>
            </Button>

            {/* Status selection menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {Object.keys(STATUS_COLORS).map(s => (
                    <MenuItem key={s} onClick={() => handleStatusChange(s)}>
                        {s}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default TaskButton;
